import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useLocation } from 'react-router-dom'
import { connect, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { selectActiveBuilding } from '../../../../../reducers/buildingSelectorReducer'
import {
  selectChecklistsSettings,
  updateChecklistsSettings,
  setSelectedChecklistTemplate,
  setSelectedChecklist,
  getSelectedChecklistTemplate,
  getSelectedChecklist,
  createNewChecklistTemplate,
  updateChecklistTemplate,
  updateChecklist,
  removeChecklist,
  getManageInspectionAllTemplates,
} from '../../../../../reducers/Organisation/checklistsReducer'
import ConfirmPopup from '../../../../../components/Popups/ConfirmPopup'
import { getManageInspectionTabs } from '../../helpers'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import ChecklistTemplateForm from '../ChecklistTemplateForm/ChecklistTemplateForm'

import ChecklistsManageInspection from './Tabs/ManageInspection/ChecklistsManageInspection'
import ChecklistsSetup from './Tabs/ChecklistsSetup/ChecklistsSetup'
import CustomTasks from './Tabs/CustomTasks/CustomTasks'

const ChecklistsSettings = ({
  checklistsSettings,
  updateChecklistsSettings,
  selectedChecklistTemplate,
  selectedChecklist,
  selectedChecklistMode,
  createNewChecklistTemplate,
  updateChecklistTemplate,
  setSelectedChecklistTemplate,
  setSelectedChecklist,
  getManageInspectionAllTemplates,
}) => {
  const { t } = useTranslation(['checklists-settings', 'checklists-legacy'])
  const manageInspectionTabs = getManageInspectionTabs(t)
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState(manageInspectionTabs[0])
  const [checklistsIsUpdated, setChecklistsIsUpdated] = useState(false)
  const [confirmVisibility, setConfirmVisibility] = useState(false)
  const [nextPath, setNextPath] = useState(null)
  const [isSiteLoading, setSiteLoading] = useState(false)
  const { displaySnackbar } = useSnackbar()

  const selectedBuilding = useSelector(selectActiveBuilding)

  useEffect(() => {
    if (selectedBuilding?.id) {
      getManageInspectionAllTemplates(selectedBuilding.id)
    }
  }, [selectedBuilding?.id])

  const showLeaveConfirmPopup = () => setConfirmVisibility(true)

  const handleBeforeUnload = useCallback(
    (e) => {
      if (checklistsIsUpdated) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    },
    [checklistsIsUpdated],
  )

  const handlePopState = useCallback(() => {
    if (
      checklistsIsUpdated &&
      !window.confirm(t('checklists-settings:shared.unsavedChangesPrompt'))
    ) {
      setNextPath({ location: location.pathname, isRoute: true })
      navigate(location.pathname)
    }
  }, [checklistsIsUpdated, location.pathname, navigate, t])

  useEffect(() => {
    if (checklistsIsUpdated) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      window.addEventListener('popstate', handlePopState)
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [checklistsIsUpdated, handleBeforeUnload, handlePopState])

  const onTabChange = (nextTab) => {
    if (checklistsIsUpdated) {
      showLeaveConfirmPopup()
      setNextPath({ location: nextTab, isRoute: false })
    } else {
      setActiveTab(nextTab)
    }
  }

  const onNext = () => {
    setConfirmVisibility(false)
    setChecklistsIsUpdated(false)

    if (nextPath) {
      if (nextPath.isRoute) {
        navigate({ pathname: nextPath.location, state: { goNext: true } })
      } else {
        setActiveTab(nextPath.location)
      }
      setNextPath(null)
    }
  }

  const onSaveChanges = async () => {
    if (selectedBuilding) {
      try {
        setSiteLoading(true)
        await updateChecklistsSettings(selectedBuilding.id, checklistsSettings.active)
        displaySnackbar(t('checklists-settings:settingsPage.saveChangesSuccess'))
      } catch ({ message }) {
        console.error(message)
        displaySnackbar(t('checklists-settings:settingsPage.saveChangesError'), true)
      } finally {
        setSiteLoading(false)
      }
    }
    onNext()
  }

  const onDiscardChanges = () => onNext()

  const renderTab = () => {
    switch (activeTab.value) {
      case 'setup':
        return (
          <ChecklistsSetup
            isSiteLoading={isSiteLoading}
            selectedBuilding={selectedBuilding}
            checklistsSettings={checklistsSettings}
            setIsUpdated={setChecklistsIsUpdated}
            onSaveChanges={onSaveChanges}
            setSiteLoading={setSiteLoading}
          />
        )
      case 'inspections':
        return <ChecklistsManageInspection />
      case 'custom':
        return <CustomTasks />
      default:
        return null
    }
  }

  useEffect(() => {
    const queryString = new URLSearchParams(location.search)
    const currentTabName = queryString.get('tab')
    const selectedTab = manageInspectionTabs.find((el) => el.value === currentTabName)

    if (selectedTab) {
      setActiveTab(selectedTab)
    }
  }, [location.search])

  useEffect(() => {
    return () => {
      setSelectedChecklistTemplate({ data: null, mode: null })
      setSelectedChecklist(null)
    }
  }, [])

  const handleCreateNewChecklistTemplate = async (sendData) => {
    try {
      await createNewChecklistTemplate(sendData)
      displaySnackbar(t('checklists-legacy:snackbars.checklistCreated'))
    } catch (error) {
      displaySnackbar(error.message || t('checklists-legacy:snackbars.checklistCreateError'), true)
    }
  }

  const handleUpdateChecklistTemplate = async (sendData, checklistId, tempCategory, onSuccess) => {
    try {
      await updateChecklistTemplate({
        checklistId,
        sendData,
        tempCategory,
      })
      displaySnackbar(t('checklists-legacy:snackbars.checklistUpdated'))
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      displaySnackbar(error.message || t('checklists-legacy:snackbars.checklistUpdateError'), true)
    }
  }

  return (
    <div className="ctp-checklists">
      <div
        className="ctp-checklists__inner"
        style={selectedChecklistTemplate || selectedChecklist ? { display: 'none' } : null}
      >
        <div className="ctp-checklists-settings">
          <div className="single-row">
            <div className="left-side">
              {manageInspectionTabs.map((tabFilter) => (
                <div
                  className={`tab-type ${activeTab.value === tabFilter.value ? 'active' : ''}`}
                  key={tabFilter.id}
                  onClick={() => onTabChange(tabFilter)}
                >
                  {tabFilter.text}
                </div>
              ))}
            </div>
          </div>
          <div className={`tab-content tab-content--${activeTab.value}`}>{renderTab()}</div>
        </div>
        <ConfirmPopup
          title={t('checklists-settings:settingsPage.cautionTitle')}
          body={t('checklists-settings:settingsPage.leaveConfirmBody')}
          cancelText={t('checklists-settings:settingsPage.discardChangesButton')}
          confirmText={t('checklists-settings:shared.saveChangesButton')}
          cancelCallback={onDiscardChanges}
          confirmCallback={onSaveChanges}
          isOpen={confirmVisibility}
          className={'is-caution'}
        />
      </div>
      {selectedChecklistTemplate && selectedChecklistMode ? (
        <ChecklistTemplateForm
          templateMode={selectedChecklistMode}
          data={selectedChecklistTemplate}
          buildingId={Number(selectedBuilding.id)}
          handleClose={() => setSelectedChecklistTemplate({ data: null, mode: null })}
          submitChecklist={
            selectedChecklistMode === 'edit'
              ? handleUpdateChecklistTemplate
              : handleCreateNewChecklistTemplate
          }
        />
      ) : null}
    </div>
  )
}

ChecklistsSettings.propTypes = {
  checklistsSettings: PropTypes.object,
  updateChecklistsSettings: PropTypes.func,
  selectedChecklistTemplate: PropTypes.object,
  selectedChecklist: PropTypes.object,
  selectedChecklistMode: PropTypes.string,
  setSelectedChecklistTemplate: PropTypes.func,
  setSelectedChecklist: PropTypes.func,
  createNewChecklistTemplate: PropTypes.func,
  updateChecklistTemplate: PropTypes.func,
  updateChecklist: PropTypes.func,
  removeChecklist: PropTypes.func,
  getManageInspectionAllTemplates: PropTypes.func,
}

export default connect(
  (state) => ({
    checklistsSettings: selectChecklistsSettings(state),
    selectedChecklist: getSelectedChecklist(state),
    selectedChecklistTemplate: getSelectedChecklistTemplate(state).data,
    selectedChecklistMode: getSelectedChecklistTemplate(state).mode,
  }),
  {
    updateChecklistsSettings,
    setSelectedChecklistTemplate,
    setSelectedChecklist,
    createNewChecklistTemplate,
    updateChecklistTemplate,
    updateChecklist,
    removeChecklist,
    getManageInspectionAllTemplates,
  },
)(ChecklistsSettings)
